import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AlertService,
    AppConfigService,
    ClaimService,
    DateParserFormatterService,
    ENDPOINTS_CONFIG,
    ErrorStateService,
    GlobalConstants,
    GuidService,
    LoggingService,
} from '@app-core';
import { Address, AddressType, Claim, ClaimEmployer, Country, Employer, Parent, State } from '@app-models';
import { FieldErrorDirective, SignaturePadComponent, ZipCodeValidator } from '@app-shared';
import { NgbDateParserFormatter, NgbDatepickerModule, NgbDateStruct, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { FraudNoticesComponent } from 'app/fraud-notices/fraud-notices.component';
import { AddressComponent } from 'app/shared/components/address/address.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-employer-response-job-relocation',
    templateUrl: './employer-response-job-relocation.component.html',
    styleUrls: ['./employer-response-job-relocation.component.scss'],
    imports: [ReactiveFormsModule, FieldErrorDirective, NgbDatepickerModule, SignaturePadComponent, AddressComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [{ provide: NgbDateParserFormatter, useClass: DateParserFormatterService }],
})
export class EmployerResponseJobRelocationComponent implements OnInit, OnDestroy {
    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly claimService = inject(ClaimService);
    private readonly appConfigService = inject(AppConfigService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly spinnerService = inject(NgxSpinnerService);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dateParser = inject(DateParserFormatterService);
    private readonly guidService = inject(GuidService);
    private readonly alertService = inject(AlertService);
    private readonly errorStateService = inject(ErrorStateService);
    private readonly modalService = inject(NgbModal);
    private claimSubscription: Subscription;
    claimTypes = GlobalConstants.CLAIM_TYPE;
    claimStatus = GlobalConstants.CLAIM_STATUS;
    claim: Claim | null = null;
    claimForm: FormGroup;
    originalFormValues: any;
    states: State[];
    filteredStates: State[];
    countries: Country[];
    stateCaption: string = 'State';
    postalCodeCaption: string = 'Zip code';

    showEmployerAddress2: boolean = false;
    showEmployeeNewAddress2: boolean = false;

    minValidDate: NgbDateStruct | null;
    maxValidDate: NgbDateStruct | null;
    minConsultDate: NgbDateStruct | null;
    settlementAuthorizationText: string = GlobalConstants.FORM_TEXT.SETTLEMENT_AUTHORIZATION;
    authorizationText: string = GlobalConstants.FORM_TEXT.AUTHORIZATION;
    instructionText: string = GlobalConstants.FORM_TEXT.EMPLOYER_INSTRUCTION;
    signatureText: string = GlobalConstants.FORM_TEXT.SIGNATURE_AKNOWLEDGEMENT;
    withdrawalSeparationDateText: string = GlobalConstants.SEPARATION_DATE_DEFINITIONS.WITHDRAWAL;
    dismissalSeparationDateText: string = GlobalConstants.SEPARATION_DATE_DEFINITIONS.DISMISSAL;

    employerCertificationText: string = GlobalConstants.FORM_TEXT.EMPLOYER_JOB_RELOCATION_CERTIFICATION;

    // Character counts for each textarea field
    characterCounts = {
        organizationName: 0,
        employerUnemploymentReason: 0,
        employerCertifyingOfficialContact: 0,
    };

    isSaving: boolean = false;

    get formControls() {
        return this.claimForm.controls;
    }

    ngOnInit(): void {
        this.fetchClaimData();
    }

    fetchClaimData(): void {
        this.states = this.appConfigService.getLookupData(ENDPOINTS_CONFIG.states);
        this.countries = this.appConfigService.getLookupData(ENDPOINTS_CONFIG.countries);
        this.minValidDate = this.getMinimumSeparationDate();
        this.maxValidDate = this.getMaximumSeparationDate();
        this.minConsultDate = this.getMinimumConsultDate();
        this.initializeClaimForm();

        this.claimSubscription = this.claimService.getCurrentClaim().subscribe((claim: Claim) => {
            if (claim) {
                this.claim = claim;
                this.claimForm.patchValue(this.claim);
                this.updateCountryCaptions(this.claim.accountParent?.addresses?.[0]?.countryISO3 || 'USA');
                // if (this.claim.parentSeparationDate) {
                //     const parsedSeparationDate = this.dateParser.parse(this.claim.parentSeparationDate);
                //     this.claimForm.get('parentSeparationDate').setValue(parsedSeparationDate);
                // }
                // TEST - pre populate form for testing
                //this.populateTestForm();
            }
        });
    }

    populateTestForm() {
        // create a test object populate the form with the values - use the ClaimEmployer model as the object
        const testClaimEmployer = new ClaimEmployer();

        testClaimEmployer.employerParent = new Parent();
        testClaimEmployer.employerParent.firstName = 'Test Parent';
        testClaimEmployer.employerParent.lastName = 'User';

        testClaimEmployer.employer = new Employer();
        testClaimEmployer.employer.organizationName = 'Test Organization';
        testClaimEmployer.employer.emailAddress = 'cartis@obitest.com';
        testClaimEmployer.employer.phoneNumber = '555-555-5555';

        testClaimEmployer.employer.addresses = [];
        const address = new Address();
        address.address1 = '123 Main St';
        address.address2 = 'Suite 100';
        address.city = 'Anytown';
        address.stateAbbreviation = 'CA';
        address.countryISO3 = 'USA';
        address.postalCode = '12345';

        const addressTypeNew = new AddressType();
        addressTypeNew.id = GlobalConstants.ADDRESS_TYPES.NEW_EMPLOYER_ADDRESS;
        addressTypeNew.description = '';
        address.addressType = addressTypeNew;
        testClaimEmployer.employer.addresses.push(address);

        // populate the remaining fields in the ClaimEmployer model
        testClaimEmployer.employerRelocationTransferDate = '2020-01-01';

        testClaimEmployer.employerCertifyingOfficialName = 'Jane Smith';
        testClaimEmployer.employerCertifyingOfficialTitle = 'HR Manager';

        testClaimEmployer.employerCertifyingOfficialContact = '555-555-5555';
        //testClaimEmployer.employerParentRelocationDate = '2024-03-16';
        //testClaimEmployer.employerRelocationTransferDate = '2024-03-17';
        // populate the remaining fields in the ClaimEmployer model using the fields below

        //populate the remaining fields in the ClaimEmployer model

        testClaimEmployer.employerSignature =
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAADICAYAAACtWK6eAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAALEgAACxIB0t1+/AAAADl0RVh0U29mdHdhcmUAQWRvYmUgSW1hZ2VSZWFkeXHJZTwAAAXWSURBVHic7d3NaxRHFAbgP3P//zO7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7e3+H4j8yM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzOzhf4H8D8J+FfgD4D8BPAfgX4F8A/AfAvwH4F+BPAPwHwL8B+BfAPwH4F8A/AfAvwH4F+BPAPwHwL8B+BfAPwH4F8A/AfAvwH4F+BPAPwHwL8B+BfAPwH4F8A/AfAvwH4F+BPAPwHwL8B+BfAPwH4F8A/AfAvwH4F+BPAPwHwL8B+BfAPwH4F8A/AfAvwH4F+BPAPwHwL8B+BeC/9X5mZmZm';

        this.claimForm.patchValue(testClaimEmployer);

        this.originalFormValues = this.claimForm.value;
        //this.changeDetector.detectChanges();

        // create this.claimForm.patchValue() entries for all date fields using the convertStringToNgbDate method
        this.claimForm.patchValue({
            employerRelocationTransferDate: this.convertStringToNgbDate(testClaimEmployer.employerRelocationTransferDate),
            employeeNewAddress: {
                address1: '1234 Main St',
                address2: 'Suite 200',
                city: 'Somewhere',
                stateAbbreviation: 'LA',
                countryISO3: 'USA',
                postalCode: '98798',
            },
            employer: {
                address: {
                    address1: address.address1,
                    address2: address.address2,
                    city: address.city,
                    stateAbbreviation: address.stateAbbreviation,
                    countryISO3: address.countryISO3,
                    postalCode: address.postalCode,
                },
            },
        });
    }

    // If you have a date string like "2024-03-15" from your API:
    private convertStringToNgbDate(dateString: string): NgbDateStruct | null {
        if (!dateString) return null;

        const parts = dateString.split('-');
        return {
            year: parseInt(parts[0], 10),
            month: parseInt(parts[1], 10),
            day: parseInt(parts[2], 10),
        };
    }
    initializeClaimForm() {
        this.claimForm = this.formBuilder.group({
            employerParent: this.formBuilder.group({
                firstName: ['', [Validators.required, Validators.maxLength(50)]],
                lastName: ['', [Validators.required, Validators.maxLength(50)]],
            }),
            employerRelocationTransferDate: ['', Validators.required],
            employeeNewAddress: this.formBuilder.group({
                address1: ['', [Validators.required, Validators.maxLength(200)]],
                address2: ['', [Validators.maxLength(200)]],
                city: ['', [Validators.required, Validators.maxLength(100)]],
                stateAbbreviation: ['', Validators.required],
                countryISO3: ['USA', Validators.required],
                postalCode: ['', [Validators.required, Validators.maxLength(10), ZipCodeValidator.validate('USA')]],
            }),

            employerCertifyingOfficialName: ['', [Validators.required, Validators.maxLength(255)]],
            employerCertifyingOfficialTitle: ['', [Validators.required, Validators.maxLength(100)]],
            employerCertifyingOfficialContact: ['', [Validators.required, Validators.maxLength(255)]],
            employer: this.formBuilder.group({
                organizationName: ['', [Validators.required, Validators.maxLength(255)]],
                address: this.formBuilder.group({
                    address1: ['', [Validators.required, Validators.maxLength(200)]],
                    address2: ['', [Validators.maxLength(200)]],
                    city: ['', [Validators.required, Validators.maxLength(100)]],
                    stateAbbreviation: ['', Validators.required],
                    countryISO3: ['USA', Validators.required],
                    postalCode: ['', [Validators.required, Validators.maxLength(10), ZipCodeValidator.validate('USA')]],
                }),
            }),

            employerSignature: ['', Validators.required],
        });

        // Initialize character counts for existing form values
        this.initializeCharacterCounts();
        //this.onValueChanges();
    }

    toggleEmployerAddress2() {
        this.showEmployerAddress2 = !this.showEmployerAddress2;
    }

    toggleEmployeeNewAddress2() {
        this.showEmployeeNewAddress2 = !this.showEmployeeNewAddress2;
    }

    // track any changes of radio buttons
    onValueChanges(): void {
        this.claimForm.get('employerEmploymentType').valueChanges.subscribe((value) => {
            if (value === 'Part Time') {
                this.claimForm.controls['employerPartTimeHoursPerWeek'].setValidators([Validators.required, Validators.pattern('^[0-9]+$')]);
            } else {
                this.claimForm.controls['employerPartTimeHoursPerWeek'].patchValue(null);
                this.claimForm.controls['employerPartTimeHoursPerWeek'].clearValidators();
            }
            this.claimForm.controls['employerPartTimeHoursPerWeek'].updateValueAndValidity();
        });
    }

    public validateDateRange(dateField1: string, dateField2: string): void {
        const startDate = new Date(this.dateParser.formatForJava(this.formControls[dateField1].value));
        const endDate = new Date(this.dateParser.formatForJava(this.formControls[dateField2].value));
        //const rangeInDays = Math.abs(startDate.getTime() - endDate.getTime()) / 1000 / 60 / 60 / 24;
        //LoggingService.log(rangeInDays);
        if (isNaN(startDate.valueOf()) || isNaN(endDate.valueOf())) {
            // Either value is not set - don't validate
            return;
        } else if (startDate.valueOf() >= endDate.valueOf()) {
            this.formControls[dateField2].setErrors({ invalidDateRange: true });
            return;
            /*         } else if (rangeInDays > 61) {
            this.formControls[dateField2].setErrors({ invalidDateSpan: true });
            return; */
        } else if (startDate.valueOf() >= endDate.valueOf()) {
            this.formControls[dateField2].setErrors({ invalidDateRange: true });
            return;
        }
        this.formControls[dateField2].setErrors(null); // Clear any previous errors
    }

    public validateDateRangeCrossField(dateField1: string, dateField2: string): boolean {
        //this.validateDateRangeCrossField('physicianLastConsultDate', 'physicianClassAbsenceEndDate');
        //
        // (ngModelChange)="validateDateRange('physicianClassAbsenceStartDate', 'physicianClassAbsenceEndDate')" />
        //(ngModelChange)="validateDateRange('physicianFirstConsultDate', 'physicianLastConsultDate')" />

        const consultDate = new Date(this.dateParser.formatForJava(this.formControls[dateField1].value));

        //let startDateNumber = startDate.getFullYear().toString() + startDate.getMonth().toString() + startDate.getDate().toString();
        const absenceDate = new Date(this.dateParser.formatForJava(this.formControls[dateField2].value));
        //let endDateNumber = endDate.getFullYear().toString() + endDate.getMonth().toString() + endDate.getDate().toString();

        const formattedConsult = this.formatDateToYYYYMMDD(consultDate);
        const formattedAbsence = this.formatDateToYYYYMMDD(absenceDate);
        //const rangeInDays = Math.abs(startDate.getTime() - endDate.getTime()) / 1000 / 60 / 60 / 24;
        //LoggingService.log(rangeInDays);
        if (isNaN(consultDate.valueOf()) || isNaN(absenceDate.valueOf())) {
            // Either value is not set - don't validate
            return true;
        } else if (formattedConsult > formattedAbsence) {
            this.formControls[dateField1].setErrors({ invalidDateConsult: true });
            return false;
        } /* else if (startDate.valueOf() >= endDate.valueOf()) {
            this.formControls[dateField2].setErrors({ invalidDateSpan: true });
            return;
        } */
        return true;
    }
    private formatDateToYYYYMMDD(date: Date): number {
        if (!date || isNaN(date.getTime())) return NaN;
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return Number(`${year}${month}${day}`);
    }

    onCountryChange(event: any) {
        const countryISO3 = event.target.value;
        this.updateCountryCaptions(countryISO3);
        this.applyZipCodeValidator(countryISO3);
    }

    onSubmit() {
        this.isSaving = true;
        this.markFormGroupTouched(this.claimForm);

        //let noErrors = this.validateDateRangeCrossField('physicianLastConsultDate', 'physicianClassAbsenceEndDate');

        if (this.claimForm.valid) {
            const claimData = this.buildClaimData();
            this.claimService.updateEmployerClaim(claimData, this.claim.id).subscribe({
                next: (response) => {
                    LoggingService.log('Application Updated', response);
                    this.claimService.setSubmissionSuccessful(true);
                    this.isSaving = false;
                    this.router.navigate(['/success'], { skipLocationChange: true });
                },
                error: (error) => {
                    this.claimService.setSubmissionSuccessful(false);
                    this.isSaving = false;
                    LoggingService.error('Error Updating Application', error);
                },
            });
        } else {
            // Reset isSaving if form is invalid
            this.isSaving = false;
            // Scroll to and focus the first invalid field
            this.scrollToFirstInvalidField();
        }
        this.originalFormValues = this.claimForm.value;
    }

    updateCharacterCount(event: Event, fieldName: keyof typeof this.characterCounts): void {
        const input = event.target as HTMLTextAreaElement;
        this.characterCounts[fieldName] = input.value.length;
    }

    private initializeCharacterCounts(): void {
        // Initialize character counts based on existing form values
        Object.keys(this.characterCounts).forEach((fieldName) => {
            const control = this.claimForm.get(fieldName);
            if (control && control.value) {
                this.characterCounts[fieldName as keyof typeof this.characterCounts] = control.value.length;
            }
        });
    }

    fraudNotice() {
        this.modalService.open(FraudNoticesComponent, { scrollable: true, size: 'xl', centered: true, fullscreen: 'sm', windowClass: 'fraud-notices-modal' });
    }

    private buildClaimData(): ClaimEmployer {
        const claimEmployer = new ClaimEmployer();
        claimEmployer.id = this.claim.id;
        const employerParent = new Parent();
        employerParent.firstName = this.claimForm.get('employerParent.firstName').value;
        employerParent.lastName = this.claimForm.get('employerParent.lastName').value;
        claimEmployer.employerParent = employerParent;

        claimEmployer.employerRelocationTransferDate = this.dateParser.format(this.claimForm.get('employerRelocationTransferDate').value);

        const employer = new Employer();
        claimEmployer.employer = employer;
        const addresses = [];

        // employees new job location
        const newAddress = new Address();
        newAddress.address1 = this.claimForm.get('employeeNewAddress.address1').value;
        newAddress.address2 = this.claimForm.get('employeeNewAddress.address2').value;
        newAddress.city = this.claimForm.get('employeeNewAddress.city').value;
        newAddress.stateAbbreviation = this.claimForm.get('employeeNewAddress.stateAbbreviation').value;
        newAddress.countryISO3 = this.claimForm.get('employeeNewAddress.countryISO3').value;
        newAddress.postalCode = this.claimForm.get('employeeNewAddress.postalCode').value;
        const addressTypeNew = new AddressType();
        addressTypeNew.id = GlobalConstants.ADDRESS_TYPES.NEW_EMPLOYER_ADDRESS;
        addressTypeNew.description = '';
        newAddress.addressType = addressTypeNew;
        addresses.push(newAddress);

        claimEmployer.employerCertifyingOfficialName = this.claimForm.get('employerCertifyingOfficialName').value;
        claimEmployer.employerCertifyingOfficialTitle = this.claimForm.get('employerCertifyingOfficialTitle').value;
        claimEmployer.employerCertifyingOfficialContact = this.claimForm.get('employerCertifyingOfficialContact').value;

        employer.organizationName = this.claimForm.get('employer.organizationName').value;

        const address = new Address();
        address.address1 = this.claimForm.get('employer.address.address1').value;
        address.address2 = this.claimForm.get('employer.address.address2').value;
        address.city = this.claimForm.get('employer.address.city').value;
        address.stateAbbreviation = this.claimForm.get('employer.address.stateAbbreviation').value;
        address.countryISO3 = this.claimForm.get('employer.address.countryISO3').value;
        address.postalCode = this.claimForm.get('employer.address.postalCode').value;
        const addressTypeOriginal = new AddressType();
        addressTypeOriginal.id = GlobalConstants.ADDRESS_TYPES.ORIGINAL_EMPLOYER_ADDRESS;
        addressTypeOriginal.description = '';
        address.addressType = addressTypeOriginal;

        addresses.push(address);

        claimEmployer.employer = employer;
        claimEmployer.employer.addresses = addresses;

        claimEmployer.employerSignature = this.claimForm.get('employerSignature').value;

        LoggingService.log(claimEmployer);
        return claimEmployer;
    }

    private getMinimumSeparationDate(): NgbDateStruct {
        return { day: 1, month: 1, year: 2020 };
    }

    private getMaximumSeparationDate(): NgbDateStruct {
        return { day: 31, month: 12, year: 2050 };
    }

    private getMinimumConsultDate(): NgbDateStruct {
        return { day: 1, month: 1, year: 2000 };
    }

    private updateCountryCaptions(countryISO3: string) {
        if (countryISO3) {
            this.stateCaption = GlobalConstants.COUNTRY_METADATA[countryISO3].stateCaption;
            this.postalCodeCaption = GlobalConstants.COUNTRY_METADATA[countryISO3].postalCodeCaption;
            this.filteredStates = this.states.filter((state) => state.country.iso3 === countryISO3).sort((a, b) => a.stateAbbreviation.localeCompare(b.stateAbbreviation));
            this.changeDetector.detectChanges();
        }
    }

    private scrollToFirstInvalidField() {
        // Find the first invalid form control (input, select, textarea)
        const firstInvalidControl = document.querySelector('input.ng-invalid, textarea.ng-invalid, select.ng-invalid, app-signature-pad.ng-invalid');

        if (firstInvalidControl) {
            // Scroll to the control
            firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Set focus on the element
            if (firstInvalidControl instanceof HTMLElement) {
                setTimeout(() => {
                    // For standard form controls
                    if (firstInvalidControl instanceof HTMLInputElement || firstInvalidControl instanceof HTMLTextAreaElement || firstInvalidControl instanceof HTMLSelectElement) {
                        firstInvalidControl.focus();
                    }
                    // For custom components like signature-pad
                    else if (firstInvalidControl.tagName.toLowerCase() === 'app-signature-pad') {
                        // Find focusable element inside the custom component
                        const canvas = firstInvalidControl.querySelector('canvas');
                        if (canvas) {
                            canvas.focus();
                        }
                    }
                }, 500); // Small delay to ensure the scroll completes first
            }
        }
    }

    private markFormGroupTouched(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach((key) => {
            const control = formGroup.get(key);
            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            } else {
                control.markAsTouched();
                control.markAsDirty();
                control.updateValueAndValidity();
            }
        });
    }

    private applyZipCodeValidator(countryISO3: string) {
        const postalCodeControl = this.claimForm.get('employer.address.postalCode');
        if (postalCodeControl) {
            postalCodeControl.setValidators([Validators.required, ZipCodeValidator.validate(countryISO3)]);
            postalCodeControl.markAsTouched();
            postalCodeControl.updateValueAndValidity();
        }
    }

    ngOnDestroy(): void {
        if (this.claimSubscription) {
            this.claimSubscription.unsubscribe();
        }
    }
}
